import CustomerViewer from 'models/customer_viewer';
import CustomerViewerActivity from 'models/customer_viewer_activity';
import CustomerViewerActivityTarget from 'models/customer_viewer_activity_target';

// An ISO-8601 instant is well under this. A cap at all because the value is
// peer-authored and reaches the store before the per-agent cap can decline it.
const MAX_ARRIVED_AT_LENGTH = 64;

/**
 * A record identifies one browser tab. `agentId` and the session both come from the
 * topic rather than the payload, and are passed in for that reason: the broker binds
 * the agent segment to the publisher's authenticated user, so it is the part of a
 * record another client cannot lie about.
 *
 * Everything else in the payload is written by a colleague's browser and checked
 * nowhere else. The model declares `oneOf` on both activity fields, but
 * `create_model` runs that validation only in dev builds, so this converter is the
 * only allowlist a deployed build has.
 */
export default {
  fromDto(dto, { agentId, sessionId, now = Date.now() } = {}) {
    return CustomerViewer.fromJs({
      id: CustomerViewer.idFor(agentId, sessionId),
      agentId,
      activity: knownActivity(dto.activity),
      activityTarget: knownActivityTarget(dto.activityTarget),
      arrivedAt: arrivalNoLaterThanNow(dto.arrivedAt, now),
    });
  },
};

// Anything unrecognised reads as viewing rather than as itself: the pill's activity
// branch treats every non-COMPOSING value as a confident "viewing" anyway, and this
// is the field that decides whether a colleague reads as looking or writing.
function knownActivity(activity) {
  return Object.values(CustomerViewerActivity).includes(activity) ? activity : CustomerViewerActivity.VIEWING;
}

function knownActivityTarget(activityTarget) {
  return Object.values(CustomerViewerActivityTarget).includes(activityTarget) ? activityTarget : undefined;
}

/**
 * Arrival, unless it is in the future, in which case absent.
 *
 * The value is minted from the publishing workstation's clock and read against
 * ours, so a peer running fast hands us an arrival that hasn't happened yet. Left
 * alone it reads as "just now" for the length of the skew and then under-reports
 * the duration by it forever — and the same value orders the pill and picks the
 * cap's eviction victim. Absent is a value every one of those consumers already
 * handles.
 *
 * A back-dated value is left alone. Any ceiling on how long an agent might
 * plausibly have been sitting on a customer would be a guess.
 */
function arrivalNoLaterThanNow(arrivedAt, now) {
  if (typeof arrivedAt !== 'string' || arrivedAt.length > MAX_ARRIVED_AT_LENGTH) {
    return undefined;
  }

  const time = new Date(arrivedAt).getTime();
  if (Number.isFinite(time) && time > now) {
    return undefined;
  }

  return arrivedAt;
}
